import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import Auth from '../../../../../api/auth';
import Table from '../../../../../components/Table';
import OrganizationBreadCrumb from '../../../../OrganizationDetails/OrganizationBreadCrumb';

import GitlabWikiHelpButton from '@/components/GitlabWikiHelpButton';
import useAppContext from '@/hooks/useAppContext';
import useTableApiCall from '@/hooks/useTableApiCall';
import { APPROVED_CALC_EXCLUSION_ROLES } from '@/rbac/rules';
import { useUserData } from '@/redux/selectors';
import type { Product } from '@/types/productTypes';
import type { TableRowData, TableField } from '@/types/tableTypes';

const productsTypeFilterMap: Record<string, string[]> = {
  caas: ['caasonopenstack', 'caasonbaremetal'],
  compute: ['evaluation', 'compute'],
  baremetal: ['baremetal'],
};

const usersGuideSlug = 'user-guide/organizations/products';

interface UserProductsProps {
  type: string;
}

const UserProducts = ({ type }: UserProductsProps): React.JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selectedOrganization } = useAppContext();
  const { organizationId, roles } = useUserData();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [rows, setRows] = useState<TableRowData[]>([]);

  const showApprovedStatus =
    !!roles && !roles.some(role => APPROVED_CALC_EXCLUSION_ROLES.includes(role));

  const transformProjectsToRows = useCallback(
    (data: Product[]) => {
      return data.map(
        ({ _id, name, description, marketplaceItem, project, details, approved }) => ({
          key: _id,
          onRowClick: () => navigate(`/products/${_id}`),
          data: [
            {
              field: 'name',
              value: name,
            },
            {
              field: 'description',
              value: description,
            },
            {
              field: 'type',
              value: marketplaceItem?.name,
              type: 'options',
            },
            {
              field: 'project',
              value: project.name,
            },
            {
              field: 'juicegroup',
              value: details.juiceGroup,
            },
            ...(type === 'compute' && showApprovedStatus
              ? [
                  {
                    field: 'approved',
                    value: approved ? 'True' : 'False',
                    type: 'options',
                  },
                ]
              : []),
          ],
        })
      );
    },
    [navigate, showApprovedStatus, type]
  );

  const handleRowsUpdate = useCallback(
    (data: Product[]) => {
      setRows(transformProjectsToRows(data) as TableRowData[]);
    },
    [transformProjectsToRows]
  );

  const memoizedDependency = useMemo(
    () => [selectedOrganization?._id ?? '', String(itemsPerPage)],
    [itemsPerPage, selectedOrganization?._id]
  );

  const { fetchData, setCurrentPage, ...tableData } = useTableApiCall(
    Auth.fetchV2Products as unknown as (...args: unknown[]) => Promise<unknown>,
    {
      isPaginated: true,
      setRows: handleRowsUpdate as (rows: unknown[]) => void,
      refetchDeps: memoizedDependency,
      enabled: !!(selectedOrganization?._id ?? organizationId),
      endpointParams: {
        types: productsTypeFilterMap[type],
        organizationId: selectedOrganization?._id ?? organizationId,
        limit: itemsPerPage,
        ...(showApprovedStatus && {
          enrich: true,
        }),
      },
      searchColumns: ['name', 'description'],
    }
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  const showOrganizationBreadCrumb = pathname.includes('/governance/organization');

  const fields = useMemo<TableField[]>(
    () => [
      { title: 'Name', id: 'name' },
      { title: 'Description', id: 'description' },
      { title: 'Type', id: 'type' },
      { title: 'Project', id: 'project' },
      { title: 'Juicegroup', id: 'juicegroup' },
      ...(type === 'compute' && showApprovedStatus ? [{ title: 'Approved', id: 'approved' }] : []),
    ],
    [showApprovedStatus, type]
  );

  return (
    <>
      {showOrganizationBreadCrumb && <OrganizationBreadCrumb />}
      <Table
        header={'Products'}
        fields={fields}
        rows={rows}
        pagination
        {...tableData}
        pageSize={itemsPerPage}
        setItemsPerPage={(size: number) => {
          setItemsPerPage(size);
        }}
        headerActionSlot={{
          content: (
            <GitlabWikiHelpButton
              slug={usersGuideSlug}
              tooltipMessageId='usersGuide'
              tooltipDefaultMessage='Users guide'
              modalTitleMessageId='usersGuide'
              modalTitleDefaultMessage='Users guide'
              buttonAriaLabel='Open users guide'
              buttonSx={{ mt: 0 }}
            />
          ),
        }}
        setFilter={tableData.setFilter as (filter: unknown) => void}
        onPageChange={handlePageChange}
        error={tableData.error ? tableData.error.message : null}
        advancedSearch={false}
      />
    </>
  );
};

export default UserProducts;
